import {Duration, getPersonInsuranceTypeFromDuration} from './duration';
import {PersonInsuranceType, RiderType} from './person-insurance';
import {RiderConfig} from './protection-request';

export interface RiderCoverage {
  amount?: number;
  duration?: number;
  type: RiderType;
}

export interface InsuranceCoverage {
  amount?: number;
  duration: Duration;
}

export interface Coverage {
  lifeInsurances: Partial<Record<Duration, number>>;
  riders?: RiderConfig[];
}

export function areCoveragesEqual(coverageA: Coverage | null, coverageB: Coverage | null): boolean {
  if (!coverageA && !coverageB) {
    return true;
  }

  if (!coverageA || !coverageB) {
    return false;
  }

  const lifeInsurancesA = Object.entries(coverageA.lifeInsurances);
  const lifeInsurancesB = Object.entries(coverageB.lifeInsurances);

  if (lifeInsurancesA.length !== lifeInsurancesB.length) {
    return false;
  }

  for (const [duration, amount] of lifeInsurancesA) {
    const matchingCoverage = lifeInsurancesB.find(([d]) => d === duration);
    if (!matchingCoverage || matchingCoverage[1] !== amount) {
      return false;
    }
  }

  if ((coverageA.riders?.length ?? 0) !== (coverageB.riders?.length ?? 0)) {
    return false;
  }

  for (const riderA of coverageA.riders ?? []) {
    const matchingRider = coverageB.riders?.find((riderB) => riderB.type === riderA.type);
    if (
      !matchingRider ||
      matchingRider.coverage !== riderA.coverage ||
      matchingRider.duration !== riderA.duration
    ) {
      return false;
    }
  }

  return true;
}

export function extractCoverageFromCoverage(
  coverage: Coverage,
  coverageType: PersonInsuranceType | RiderType,
): {coverageAmount: number; duration?: Duration | number} | undefined {
  if (coverageType === 'WHOLE' || coverageType === 'TERM') {
    if (!coverage.lifeInsurances) {
      return undefined;
    }

    const duration = Object.keys(coverage.lifeInsurances).find(
      (duration) => getPersonInsuranceTypeFromDuration(duration) === coverageType,
    );

    if (!duration) {
      return undefined;
    }

    return {
      duration: duration as Duration,
      coverageAmount: coverage.lifeInsurances[duration],
    };
  } else {
    const rider = coverage.riders?.find((r) => r.type === coverageType);
    if (!rider) {
      return undefined;
    }

    return {
      coverageAmount: rider.coverage,
      duration: rider.duration,
    };
  }
}
